import { defineStore } from 'pinia'
import { useNuxtApp } from '#imports'
import { doc, setDoc, getDocs, collection, query, where, serverTimestamp } from 'firebase/firestore'

export interface VisitorActivity {
  path: string
  timestamp: number
  timeSpent?: number
}

export interface LeadData {
  id?: string
  sessionId: string
  name?: string
  email?: string
  phone?: string
  source: string
  interestedProduct?: string
  status: 'New' | 'Contacted' | 'Interested' | 'Purchased' | 'Not Interested'
  visitedPages: string[]
  lastActivity: any
  deviceInfo: string
  location?: string
  isAnonymous: boolean
  score: number
  recentlyViewedProducts: string[]
  searchKeywords: string[]
  assignedTo?: string
  notes?: {text: string, date: string, author: string}[]
  followUps?: {date: string, type: string, status: string}[]
}

const generateSessionId = () => {
  return 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36)
}

export const useTrackingStore = defineStore('tracking', {
  state: () => ({
    consentGranted: false,
    sessionId: '',
    location: '',
    deviceInfo: '',
    activities: [] as VisitorActivity[],
    lastSyncTime: 0,
    currentLeadData: null as Partial<LeadData> | null,
    score: 0,
    recentlyViewedProducts: [] as string[],
    searchKeywords: [] as string[],
    lastCaptureTime: 0
  }),

  actions: {
    async initialize() {
      if (!process.client) return
      
      const consent = localStorage.getItem('tracking_consent')
      if (consent === 'granted') {
        this.consentGranted = true
        this.sessionId = localStorage.getItem('tracking_session') || generateSessionId()
        localStorage.setItem('tracking_session', this.sessionId)
        
        this.deviceInfo = navigator.userAgent
        
        if (!localStorage.getItem('tracking_location')) {
          try {
            const res = await fetch('https://ipapi.co/json/')
            const data = await res.json()
            this.location = `${data.city}, ${data.country_name}`
            localStorage.setItem('tracking_location', this.location)
          } catch (e) {
            console.error('Failed to get location', e)
          }
        } else {
          this.location = localStorage.getItem('tracking_location') || ''
        }

        // Initialize lead in Firestore if not exists
        this.syncToFirestore(true)
      }
    },

    grantConsent() {
      if (process.client) {
        localStorage.setItem('tracking_consent', 'granted')
        this.initialize()
      }
    },

    revokeConsent() {
      if (process.client) {
        localStorage.removeItem('tracking_consent')
        localStorage.removeItem('tracking_session')
        this.consentGranted = false
        this.sessionId = ''
      }
    },

    logPageView(path: string) {
      if (!this.consentGranted) return

      const now = Date.now()
      
      // Update time spent on previous page
      if (this.activities.length > 0) {
        const last = this.activities[this.activities.length - 1]
        if (last) last.timeSpent = now - last.timestamp
      }

      this.activities.push({
        path,
        timestamp: now
      })

      // Sync every 5 page views or first page view
      if (this.activities.length % 5 === 0 || this.activities.length === 1) {
        this.syncToFirestore()
      }
    },

    logAction(action: string, productContext?: string) {
      if (!this.consentGranted) return
      
      this.activities.push({
        path: `[ACTION] ${action}${productContext ? ': ' + productContext : ''}`,
        timestamp: Date.now()
      })
      this.syncToFirestore()
    },

    addScore(points: number, reason: string) {
      if (!this.consentGranted) return
      this.score += points
      this.logAction(`Earned ${points} points`, reason)
    },

    addRecentlyViewed(productName: string) {
      if (!this.recentlyViewedProducts.includes(productName)) {
        this.recentlyViewedProducts.unshift(productName)
        if (this.recentlyViewedProducts.length > 10) this.recentlyViewedProducts.pop()
      }
    },

    addSearchKeyword(keyword: string) {
      if (!this.searchKeywords.includes(keyword)) {
        this.searchKeywords.unshift(keyword)
        if (this.searchKeywords.length > 20) this.searchKeywords.pop()
      }
    },

    async captureLead(contactData: { name?: string, phone?: string, email?: string, source: string, interestedProduct?: string }) {
      // Rate limiting: max 1 per 60 seconds
      const now = Date.now()
      if (now - this.lastCaptureTime < 60000 && this.currentLeadData && !this.currentLeadData.isAnonymous) {
          console.warn("Rate limited lead capture.")
          return
      }
      this.lastCaptureTime = now

      if (!this.sessionId) {
          this.sessionId = generateSessionId()
          this.deviceInfo = process.client ? navigator.userAgent : 'Unknown'
      }

      const { $db } = useNuxtApp() as any
      const leadsCol = collection($db, 'leads')

      // Duplicate Detection
      if (contactData.email || contactData.phone) {
        try {
          const emailQuery = contactData.email ? query(leadsCol, where('email', '==', contactData.email)) : null
          const phoneQuery = contactData.phone ? query(leadsCol, where('phone', '==', contactData.phone)) : null
          
          let existingDocId = null
          let existingScore = 0

          if (emailQuery) {
              const snap = await getDocs(emailQuery)
              if (!snap.empty && snap.docs[0]) {
                  existingDocId = snap.docs[0]!.id
                  existingScore = snap.docs[0]!.data()?.score || 0
              }
          }
          if (!existingDocId && phoneQuery) {
              const snap = await getDocs(phoneQuery)
              if (!snap.empty && snap.docs[0]) {
                  existingDocId = snap.docs[0]!.id
                  existingScore = snap.docs[0]!.data()?.score || 0
              }
          }

          if (existingDocId && existingDocId !== this.sessionId) {
              // Switch our session to the existing doc to merge
              this.sessionId = existingDocId
              this.score += existingScore
          }
        } catch (e) {
          console.error("Duplicate check failed", e)
        }
      }

      this.currentLeadData = {
        ...this.currentLeadData,
        ...contactData,
        isAnonymous: false,
        status: 'New'
      }

      this.syncToFirestore(true)
    },

    syncToFirestore(force = false) {
      if (!this.sessionId) return
      
      const now = Date.now()
      if (!force && now - this.lastSyncTime < 5000) return
      this.lastSyncTime = now

      const syncTask = async () => {
        try {
          const { $db } = useNuxtApp() as any
          const docRef = doc($db, 'leads', this.sessionId)
          
          const payload: any = {
            sessionId: this.sessionId,
            lastActivity: serverTimestamp(),
            visitedPages: this.activities.map(a => a.path),
            deviceInfo: this.deviceInfo,
            location: this.location,
            score: this.score,
            recentlyViewedProducts: this.recentlyViewedProducts,
            searchKeywords: this.searchKeywords,
            assignedTo: this.currentLeadData?.assignedTo || '',
            notes: this.currentLeadData?.notes || [],
            followUps: this.currentLeadData?.followUps || []
          }

          if (this.currentLeadData) {
            Object.assign(payload, this.currentLeadData)
          } else {
            payload.isAnonymous = true
            if (!payload.status) payload.status = 'New'
          }

          await setDoc(docRef, payload, { merge: true })
        } catch (error) {
          console.error('Failed to sync tracking data:', error)
        }
      }

      // Background optimization
      if (process.client && 'requestIdleCallback' in window) {
          (window as any).requestIdleCallback(() => syncTask())
      } else {
          setTimeout(syncTask, 0)
      }
    }
  }
})
